import React, { useState, useEffect } from "react";
import { Button, Modal, Form, NavDropdown } from "react-bootstrap";
import { useUser } from "../UserContext";
import { API_URL } from "../../shared/apiConfig";
import "./ModalLoginComponent.css";


function LoginModalComponent() {
    // State for toggling login and register modals
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showRegisterModal, setShowRegisterModal] = useState(false);

    // States for user input fields
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [registerUsername, setRegisterUsername] = useState("");
    const [registerPassword, setRegisterPassword] = useState("");

    // State for error messages and logged-in user information
    const [error, setError] = useState("");
    const [userName, setUserName] = useState(null);

    const { refreshUserDetails, setUser } = useUser();

    // Fetch user identity using JWT
    const fetchUserIdentity = async () => {
        const token = localStorage.getItem("token");

        if (!token) {
            setUserName(null);
            return;
        }

        try {
            const response = await fetch(`${API_URL}/auth/user`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                }
            });

            if (response.ok) {
                const data = await response.json();
                setUserName(data.name);
            } else if (response.status === 401 || response.status === 404) {
                setUserName(null); // Token invalid or expired
            } else {
                console.error("Unexpected response:", response.status);
                setUserName(null);
            }
        } catch (error) {
            console.error("Error fetching user identity:", error);
            setUserName(null);
        }
    };

    useEffect(() => {
        fetchUserIdentity();
    }, []);

    // Handle user login
    const handleLogin = async (e) => {
        e.preventDefault();
        setError("");

        try {
            const response = await fetch(`${API_URL}/auth/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ username, password, rememberMe: true }),
            });

            if (response.ok) {
                const data = await response.json();

                const token = data.message;

                if (!token) {
                    throw new Error("Token not found in response");
                }

                // Store the JWT token in localStorage
                localStorage.setItem("token", token);

                // Decode the JWT token to extract the role
                const decodedToken = JSON.parse(atob(token.split(".")[1])); // Decode the payload
                const userRole = decodedToken["role"];

                // Store the role in localStorage
                localStorage.setItem("role", userRole);

                // Update global user state, redirect to homepage and close the modal
                await refreshUserDetails();
                setShowLoginModal(false);
                window.location.href = "/"; // Redirect to homepage

            } else {
                const errorData = await response.json();
                setError("Login failed: " + (errorData.message || "Unknown error"));
            }
        } catch (error) {
            console.error("Error during login:", error);
            setError("An error occurred. Please try again.");
        }
    };

    // Validate password for registration
    function validatePassword(password) {
        const errors = [];
        if (password.length < 6) errors.push("Minst 6 tegn");
        if (!/[A-Z]/.test(password)) errors.push("Minst én stor bokstav");
        if (!/\d/.test(password)) errors.push("Minst ett tall");
        if (!/[^A-Za-z0-9]/.test(password)) errors.push("Minst ett spesialtegn");
        return errors;
    }

    // Handle user registration
    const handleRegister = async (e) => {
        e.preventDefault();
        setError("");

        // password validation
        const passwordErrors = validatePassword(registerPassword);
        if (passwordErrors.length > 0) {
            setError("Passordet må ha: " + passwordErrors.join(", "));
            return;
        }

        const payload = { username: registerUsername, password: registerPassword };

        try {
            const response = await fetch(`${API_URL}/auth/register`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                setShowRegisterModal(false);
                setShowLoginModal(true);
            } else {
                const errorData = await response.json();
                console.error("Registration failed:", errorData);

                // Check if it's a validation error
                if (errorData.errors) {
                    // For example, if the "Username" field failed
                    const usernameError = errorData.errors.Username?.[0];
                    const passwordError = errorData.errors.Password?.[0];

                    if (usernameError) {
                        setError("The field Username must be a minimum length of 4.");
                    } else if (passwordError) {
                        setError("The field Password must be minimum length of 6.");
                    } else {
                        // Some other validation
                        setError("Registration failed: unknown validation error");
                    }
                } else {
                    // Fallback if there's no 'errors' object
                    setError("Registration failed: " + (errorData.message || "Unknown error"));
                }
            }
        } catch (error) {
            console.error("Error during registration:", error);
            setError("An error occurred. Please try again.");
        }
    };

    // Handle user logout
    const handleLogout = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_URL}/auth/logout`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
            });
            // Remove JWT and role from localStorage regardless of response
            localStorage.removeItem("token");
            localStorage.removeItem("role");
            setUser(null);
            setUserName(null);
            if (!response.ok) {
                setError("Logout failed");
            }
        } catch (error) {
            // Remove JWT and role even if fetch fails
            localStorage.removeItem("token");
            localStorage.removeItem("role");
            setUser(null);
            setUserName(null);
            console.error("Error during logout:", error);
            setError("An error occurred. Please try again.");
        }
    };

    return (
        <>
            <div className="top-right-corner">
                {userName ? (
                    <>
                        <span className="me-2">Welcome, {userName}</span>
                        <Button variant="outline-danger" onClick={handleLogout}>
                            Logout
                        </Button>
                    </>
                ) : (
                    <Button variant="outline-primary" onClick={() => setShowLoginModal(true)}>
                        Login
                    </Button>
                )}
            </div>

            {/* Add to Hamburger Menu for small screens */}
            <div className="d-lg-none">
                {userName ? (
                    <NavDropdown title="Account" id="user-nav-dropdown">
                        <NavDropdown.Item disabled>{userName}</NavDropdown.Item>
                        <NavDropdown.Divider />
                        <NavDropdown.Item>
                            <Button variant="outline-danger" onClick={handleLogout}>
                                Logout
                            </Button>
                        </NavDropdown.Item>
                    </NavDropdown>
                ) : (
                    <NavDropdown title="Account" id="guest-nav-dropdown">
                        <NavDropdown.Item>
                            <Button variant="outline-primary" onClick={() => setShowLoginModal(true)}>
                                Login
                            </Button>
                        </NavDropdown.Item>
                    </NavDropdown>
                )}
            </div>

            {/* Login Modal */}
            <Modal show={showLoginModal} onHide={() => setShowLoginModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Login</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleLogin}>
                        <Form.Group controlId="formBasicUsername">
                            <Form.Label>Username</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <Form.Group controlId="formBasicPassword">
                            <Form.Label>Password</Form.Label>
                            <Form.Control
                                type="password"
                                placeholder="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                        </Form.Group>
                        {error && <p style={{ color: "red" }}>{error}</p>}
                        <Button variant="primary" type="submit" className="mt-3">
                            Login
                        </Button>
                    </Form>
                    <div className="text-center mt-3">
                        <button
                            type="button"
                            className="link-button"
                            onClick={() => {
                                setShowLoginModal(false);
                                setShowRegisterModal(true);
                            }}
                        >
                            Don't have an account? Register here
                        </button>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Registration Modal */}
            <Modal show={showRegisterModal} onHide={() => setShowRegisterModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Register</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleRegister}>
                        <Form.Group controlId="registerUsername">
                            <Form.Label>Username</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter username"
                                value={registerUsername}
                                onChange={(e) => setRegisterUsername(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <Form.Group controlId="registerPassword">
                            <Form.Label>Password</Form.Label>
                            <Form.Control
                                type="password"
                                placeholder="Password"
                                value={registerPassword}
                                onChange={(e) => setRegisterPassword(e.target.value)}
                                required
                            />
                        </Form.Group>
                        {error && <p style={{ color: "red" }}>{error}</p>}
                        <Button variant="primary" type="submit" className="mt-3">
                            Register
                        </Button>
                    </Form>
                    <div className="text-center mt-3">
                        <button
                            type="button"
                            className="link-button"
                            onClick={() => {
                                setShowRegisterModal(false);
                                setShowLoginModal(true);
                            }}
                        >
                            Already have an account? Login here
                        </button>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
}

export default LoginModalComponent;